import { useMemo } from "react"
import PropTypes from "prop-types"
import { Toolbar, Button, IconButton } from "@mui/material"
import { intFormatter } from "helpers/formatters/numbers"
import { ViewWeek, Delete, CloudDownload, OfflineBolt } from "@mui/icons-material"
import Tooltip from "components/material/tooltip"
import classNames from "classnames"
import pluralize from "pluralize"
import WatchlistsMenu from "modules/watchlists-menu"
import "./styles.scss"
import { TOOLTIP_ENTER_DELAY_MS } from "helpers/constants"

export default function ToolbarHeader({ watchlistsMenuVisible, heapCreateWatchlistId, heapAddToWatchlistId, toggleWatchlistsMenu, active, selectedSecurities, selectedSecuritiesTotal, onColumnSelectorToggle, title, titleTooltip, disableColumnSelection, onDeleteSelection, watchlistToFilter, selectedDate, download, count, customActions, csvDownloadLabel, maxSecuritiesDownload = 5000, modelId }) {
  const securitiesLabel = active ? `${intFormatter(selectedSecuritiesTotal)} ${pluralize("security", selectedSecuritiesTotal)} selected` : title

  const downloadTitle = useMemo(() => {
    if (csvDownloadLabel) {
      return csvDownloadLabel
    }
    if (count > maxSecuritiesDownload) {
      return `Only the top ${maxSecuritiesDownload} securities will be downloaded`
    }
    return "Download Contributors"
  }, [count, csvDownloadLabel, maxSecuritiesDownload])

  const downloadNode = useMemo(() => {
    if (active) { return null }
    return <Tooltip enterDelay={TOOLTIP_ENTER_DELAY_MS} title={downloadTitle}>
      <IconButton onClick={download} id="table-excel-download" size="large">
        <CloudDownload />
      </IconButton>
    </Tooltip>
  }, [active, download, downloadTitle])

  const content = useMemo(() => {
    if (active) {
      return <>
        {onDeleteSelection && <div className="delete-wrapper">
          <Tooltip title={`Remove selected ${pluralize("security", selectedSecuritiesTotal)} from watchlist`}>
            <IconButton onClick={onDeleteSelection} size="large">
              <Delete />
            </IconButton>
          </Tooltip>
        </div>}
        <WatchlistsMenu modelId={modelId} heapCreateWatchlistId={heapCreateWatchlistId} heapAddToWatchlistId={heapAddToWatchlistId} noSpinner securities={selectedSecurities} watchlistToFilter={watchlistToFilter} menuVisible={watchlistsMenuVisible} toggleMenu={toggleWatchlistsMenu} asOf={selectedDate} />
      </>
    }

    return <>
      {downloadNode}
      {titleTooltip && <Tooltip enterDelay={TOOLTIP_ENTER_DELAY_MS} title={titleTooltip}>
        <OfflineBolt className="warning" />
      </Tooltip>}
    </>
  }, [active, downloadNode, modelId, heapAddToWatchlistId, heapCreateWatchlistId, onDeleteSelection, selectedDate, selectedSecurities, titleTooltip, toggleWatchlistsMenu, watchlistToFilter, watchlistsMenuVisible, selectedSecuritiesTotal])

  return <Toolbar classes={{ gutters: "condensed-gutters" }} className={classNames("components_material-data-table-toolbar", { active })}>
    <div className="securities-count">
      {securitiesLabel}
    </div>
    {content}
    <div className="actions">
      {customActions}
      {!disableColumnSelection && <Button className="columns" onClick={onColumnSelectorToggle}>
        Columns
        <ViewWeek />
      </Button>}
    </div>
  </Toolbar>
}

ToolbarHeader.propTypes = {
  active: PropTypes.bool,
  selectedSecuritiesTotal: PropTypes.number,
  selectedSecurities: PropTypes.object,
  onColumnSelectorToggle: PropTypes.func,
  heapCreateWatchlistId: PropTypes.string,
  heapAddToWatchlistId: PropTypes.string,
  onDeleteSelection: PropTypes.func,
  disableColumnSelection: PropTypes.bool,
  watchlistToFilter: PropTypes.string,
  title: PropTypes.string,
  titleTooltip: PropTypes.string,
  watchlistsMenuVisible: PropTypes.bool,
  toggleWatchlistsMenu: PropTypes.func,
  selectedDate: PropTypes.string,
  download: PropTypes.func.isRequired,
  count: PropTypes.number,
  customActions: PropTypes.object,
  csvDownloadLabel: PropTypes.string,
  maxSecuritiesDownload: PropTypes.number,
  modelId: PropTypes.string.isRequired,
}
